import Highcharts from 'highcharts';
import { capitalDesignTokens } from "@opengov/capital-mui-theme";
import { ProgressTrackerSettingType, TimelineChartOrientation } from '../types';

export function dataLabelSideForIndex(status: string, progressTrackerSettings: ProgressTrackerSettingType[], orientation: TimelineChartOrientation) {

	const statusItem = progressTrackerSettings.find((setting: ProgressTrackerSettingType) => setting.id === status);
	if (statusItem && statusItem.verticalLabelPosition && statusItem.horizontalLabelPosition) {
		if (orientation === TimelineChartOrientation.VERTICAL) {
			return {
				align: statusItem.verticalLabelPosition === 'left' ? 'right' : 'left',
				verticalAlign: 'middle',
				x: statusItem.verticalLabelPosition === 'left' ? -30 : 30,
				y: 0
			};
		} else {
			// Horizontal timeline - labels on top or bottom
			return {
				align: 'center',
				verticalAlign: statusItem.horizontalLabelPosition === 'top' ? 'bottom' : 'top',
				x: 0,
				y: statusItem.horizontalLabelPosition === 'top' ? -50 : 50
			};
		}
	}
}

interface TimelinePoint extends Highcharts.Point {
	description?: string;
}

export function isLabelOnTop(hoverPoint: Highcharts.Point | null): boolean {
	const dataLabels = hoverPoint?.options?.dataLabels as Highcharts.DataLabelsOptions;
	return dataLabels?.verticalAlign === 'bottom' || (dataLabels?.y !== undefined && dataLabels.y < 0);
}

export function getTooltipPositioner(): Highcharts.TooltipPositionerCallbackFunction {
	return function (labelWidth: number, labelHeight: number, point: Highcharts.TooltipPositionerPointObject) {
		const chart = this.chart;
		const plotX = point.plotX ?? 0;
		const plotY = point.plotY ?? 0;

		// Get the hovered point's dataLabels to determine label position
		const hoverPoint = chart.hoverPoint;
		const labelOnTop = isLabelOnTop(hoverPoint);

		// Position tooltip OPPOSITE to label
		const tooltipOffset = 0;
		const x = chart.plotLeft + plotX - labelWidth / 2;
		const y = labelOnTop
			? chart.plotTop + plotY + tooltipOffset  // Label is top, tooltip goes bottom
			: chart.plotTop + plotY - labelHeight - tooltipOffset;  // Label is bottom, tooltip goes top

		return { x, y };
	};
}

export function getTooltipFormatter() {
	return function (this: Highcharts.TooltipFormatterContextObject): string {
		const point = this.point as TimelinePoint;
		const labelOnTop = isLabelOnTop(point);
		const description = point?.description || '';

		// Arrow styles - pointing up when tooltip is below, pointing down when tooltip is above
		const arrowUp = `
			<div style="
				width: 0;
				height: 0;
				border-left: 8px solid transparent;
				border-right: 8px solid transparent;
				border-bottom: 8px solid #fff;
				margin: 0 auto;
				filter: drop-shadow(0 -1px 1px rgba(0,0,0,0.1));
			"></div>
		`;

		const arrowDown = `
			<div style="
				width: 0;
				height: 0;
				border-left: 8px solid transparent;
				border-right: 8px solid transparent;
				border-top: 8px solid #fff;
				margin: 0 auto;
				filter: drop-shadow(0 1px 1px rgba(0,0,0,0.1));
			"></div>
		`;

		const content = `
			<div style="
				background: #fff;
				color: #333;
				padding: 8px 12px;
				border-radius: 4px;
				font-size: 14px;
				max-width: 200px;
				text-align: center;
				box-shadow: 0 2px 8px rgba(0,0,0,0.15);
			">${description}</div>
		`;

		// If label is on top, tooltip is below → arrow points up
		// If label is on bottom, tooltip is above → arrow points down
		if (labelOnTop) {
			return `<div style="text-align: center;">${arrowUp}${content}</div>`;
		} else {
			return `<div style="text-align: center;">${content}${arrowDown}</div>`;
		}
	};
}


// Reusable function to define custom rounded square symbol
export const defineRoundedSquareSymbol = () => {
	if (typeof Highcharts !== 'undefined' && Highcharts.SVGRenderer) {
		Highcharts.SVGRenderer.prototype.symbols.roundedSquare = function (x: number, y: number, w: number, h: number) {
			const r = 5; // Border radius
			return [
				'M', x + r, y,
				'L', x + w - r, y,
				'Q', x + w, y, x + w, y + r,
				'L', x + w, y + h - r,
				'Q', x + w, y + h, x + w - r, y + h,
				'L', x + r, y + h,
				'Q', x, y + h, x, y + h - r,
				'L', x, y + r,
				'Q', x, y, x + r, y,
				'Z'
			];
		};
	}
};

// JavaScript string version for embedding in HTML
export const getRoundedSquareSymbolScript = () => `
// Define custom rounded square symbol for timeline markers
if (typeof Highcharts !== 'undefined' && Highcharts.SVGRenderer) {
	Highcharts.SVGRenderer.prototype.symbols.roundedSquare = function (x, y, w, h) {
		var r = 5; // Border radius
		return [
			'M', x + r, y,
			'L', x + w - r, y,
			'Q', x + w, y, x + w, y + r,
			'L', x + w, y + h - r,
			'Q', x + w, y + h, x + w - r, y + h,
			'L', x + r, y + h,
			'Q', x, y + h, x, y + h - r,
			'L', x, y + r,
			'Q', x, y, x + r, y,
			'Z'
		];
	};
}`;

// Custom rounded square symbol for timeline markers
// Only define if Highcharts is available
defineRoundedSquareSymbol();

export const EVENT_STATUS_OPTIONS = [
	'Complete',
	'In Progress',
	'Not Started'
];

export type EventStatus = typeof EVENT_STATUS_OPTIONS[number];

export const STATUS_COLORS: Record<EventStatus, string> = {
	'Complete': capitalDesignTokens.semanticColors.foreground.successLarge,
	'In Progress': capitalDesignTokens.semanticColors.foreground.inProgressLarge,
	'Not Started': capitalDesignTokens.semanticColors.foreground.disabled
};

export const getStatusColor = (statusId: EventStatus | string, progressTrackerSettings: ProgressTrackerSettingType[]): string => {
	const color = progressTrackerSettings.find((setting: ProgressTrackerSettingType) => setting.id === statusId)?.colorCode;
	if (color) {
		return color;
	}
	return STATUS_COLORS['Not Started'];
};
export const getStatusTitle = (statusId: EventStatus | string, progressTrackerSettings: ProgressTrackerSettingType[]): string => {
	const title = progressTrackerSettings.find((setting: ProgressTrackerSettingType) => setting.id === statusId)?.status;
	if (title) {
		return title;
	}
	return 'Not Started';
};
export const capitalize = (str: string) => {
	return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}
// Shared chart configuration for all timeline charts
export const SHARED_TIMELINE_CHART_CONFIG = {
	chart: {
		type: 'timeline',
		height: 200
	},
	xAxis: {
		visible: false
	},
	yAxis: {
		visible: false
	},
	plotOptions: {
		timeline: {
			colorByPoint: true
		},
		series: {
			animation: false
		}
	},
	series: [{
		type: 'timeline',
		dataLabels: {
			allowOverlap: false,
			enabled: true,
			format: '{point.name}',
			style: {
				fontSize: '12px',
				fontWeight: 'bold'
			}
		},
		marker: {
			symbol: 'roundedSquare',
			radius: 8,
			lineWidth: 2,
			lineColor: '#ffffff'
		},
		states: {
			hover: {
				lineWidthPlus: 0
			}
		},
		tooltip: {
			headerFormat: '',
			pointFormat: '{point.description}'
		}
	}],
	credits: {
		enabled: false
	}
};

export const getProgressTrackerChart = (title: string, timelineData: any) => {
	return {
		...SHARED_TIMELINE_CHART_CONFIG,
		title: {
			text: title || '',
			align: 'left',
		},
		series: [{
			...SHARED_TIMELINE_CHART_CONFIG.series[0],
			data: timelineData
		}]
	};
};